// Mapbox canvas with refined light styling. A single GeoJSON source feeds:
//   1. cluster bubbles (low zooms)
//   2. shadow drop      (subtle elevation under each marker)
//   3. base point       (every event)
//   4. selected ring    (indigo ring around selected event)
//   5. signal pulse     (active events, animated)
//
// All rendering is WebGL — no DOM markers, no per-frame React reconciles.
import { useEffect, useRef } from 'react';
import mapboxgl, { type GeoJSONSource, type Map as MapboxMap } from 'mapbox-gl';
import type { EventRecord, Category } from '../lib/events';
import { CATEGORY_COLOR, markerRadius } from '../lib/palette';
import { totalCases } from '../lib/events';
import { config } from '../lib/config';
import type { StatusDimension } from './country-status-switcher';

// CountryStatusRecord — the shape of records in frontend/public/data/country-status.json
export interface CountryStatusRecord {
  country_iso2: string;
  burden?: {
    outbreak_count?: number | null;
    total_deaths?: number | null;
    first_outbreak_year?: number | null;
    last_outbreak_year?: number | null;
  };
  recency?: {
    years_since_last_outbreak?: number | null;
    currently_active?: boolean | null;
  };
  vaccine_readiness?: {
    ervebo_stockpile_doses?: number | null;
    national_reserves?: boolean | null;
    sudan_vaccine_trials?: boolean | null;
  };
  preparedness?: {
    jee_score?: number | null;
    jee_year?: number | null;
    ihr_capacity_pct?: number | null;
  };
}

interface Props {
  events: EventRecord[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** 'markers' shows the discrete circles; 'heatmap' shows a density
   *  gradient weighted by case count; 'choropleth' fills country
   *  polygons by total case count. */
  viewMode?: 'markers' | 'heatmap' | 'choropleth';
  /** Initial camera position. Driven by URL params lat/lng/zoom on landing
   *  pages (e.g. /where/dubai → opens map centered on Dubai). Falls back to
   *  the global default when not supplied. */
  initialCamera?: { lat: number; lng: number; zoom: number };
  /** Active status dimension for the choropleth layer (cage-bend #2).
   *  When viewMode === 'choropleth', the fill expression is derived from
   *  countryStatus keyed by this dimension. Defaults to 'burden'. */
  statusDim?: StatusDimension;
  /** Country-status records keyed by country_iso2. Loaded from
   *  /data/country-status.json. Used to colour the choropleth per dimension. */
  countryStatus?: Record<string, CountryStatusRecord>;
}

// Two basemap styles. The choropleth view swaps to the full streets
// style — coloured terrain, water, road network — so the country fills
// sit on top of recognisable cartography. Markers + heatmap stay on the
// cosmic satellite globe. Switching styles re-fires `style.load`, which
// re-runs installLayers + pushData + applyViewMode below.
const STYLE_SATELLITE = config.map.styleDark;
const STYLE_STREETS = config.map.styleLight;

function styleForMode(mode: 'markers' | 'heatmap' | 'choropleth'): string {
  return mode === 'choropleth' ? STYLE_STREETS : STYLE_SATELLITE;
}

const SOURCE = 'events';
// The clustered source above only emits cluster features at low zoom,
// which makes the heatmap layer (filtered to non-cluster points) invisible
// when zoomed out. A second non-clustered source backs the heatmap so
// every event contributes its weight at every zoom.
const SOURCE_FLAT = 'events-flat';
const SOURCE_COUNTRIES = 'mapbox-country-boundaries';
const LAYER_HEATMAP = 'events/heatmap';
const LAYER_CHOROPLETH = 'events/choropleth';
const LAYER_CHOROPLETH_OUTLINE = 'events/choropleth-outline';
const LAYER_CLUSTER_GLOW = 'events/cluster-glow';
const LAYER_CLUSTER_BG = 'events/cluster-bg';
const LAYER_CLUSTER_RING = 'events/cluster-ring';
const LAYER_CLUSTER_COUNT = 'events/cluster-count';
const LAYER_PULSE = 'events/pulse';
const LAYER_SHADOW = 'events/shadow';
const LAYER_SELECTED = 'events/selected';
const LAYER_BASE = 'events/base';

const MARKER_LAYERS = [
  LAYER_CLUSTER_GLOW,
  LAYER_CLUSTER_BG,
  LAYER_CLUSTER_RING,
  LAYER_CLUSTER_COUNT,
  LAYER_PULSE,
  LAYER_SHADOW,
  LAYER_SELECTED,
  LAYER_BASE,
] as const;

const CHOROPLETH_LAYERS = [LAYER_CHOROPLETH, LAYER_CHOROPLETH_OUTLINE] as const;

/**
 * Aggregate per-country total case counts (confirmed + suspected) across
 * the full `countries_affected` array, not just `country_iso2`. This is
 * the right denominator for a choropleth — an event that lists 10
 * countries (e.g. MV Hondius) should paint all 10, not just the one its
 * marker sits in.
 */
function casesByCountry(rows: EventRecord[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const o of rows) {
    const total = totalCases(o);
    if (total <= 0) continue;
    for (const code of o.countries_affected) {
      if (!code || code === 'INTL') continue;
      out.set(code.toUpperCase(), (out.get(code.toUpperCase()) ?? 0) + total);
    }
  }
  return out;
}

/**
 * Build a Mapbox `match` expression that maps each polygon's
 * iso_3166_1_alpha_3 / iso_3166_1 to a colour bucket. Countries with
 * zero (or missing) cases get a near-transparent grey so the underlying
 * basemap reads through. The buckets are log-scaled so a single 3,000-
 * case season doesn't flatten the rest of the data into the same red.
 */
function buildChoroplethColorExpression(byCountry: Map<string, number>): mapboxgl.ExpressionSpecification {
  const cases: Array<[string, string]> = [];
  for (const [code, count] of byCountry.entries()) cases.push([code, choroplethColor(count)]);
  if (cases.length === 0) {
    return 'rgba(156, 163, 175, 0.18)' as unknown as mapboxgl.ExpressionSpecification;
  }
  // ['match', ['get', 'iso_3166_1'], 'AR', '#ef4444', 'CL', '#fb923c', …, /* default */ transparent grey]
  const expr: unknown[] = ['match', ['get', 'iso_3166_1']];
  for (const [code, color] of cases) {
    expr.push(code, color);
  }
  expr.push('rgba(156, 163, 175, 0.0)'); // transparent default — basemap shows through
  return expr as unknown as mapboxgl.ExpressionSpecification;
}

/**
 * Continuous (smooth) choropleth colour, log-scaled between two stops.
 * Replaces the previous five-bucket ramp so countries with neighbouring
 * case counts no longer jump to wildly different colours. The full
 * gradient runs grey (no data) → soft yellow (~1 case) → orange → red →
 * deep red (~30,000 case epidemic). Opacity climbs with intensity.
 */
function choroplethColor(cases: number): string {
  if (cases <= 0) return 'rgba(156, 163, 175, 0.16)';
  // Log-normalise to [0, 1]. log10(30,000) ≈ 4.48, so we divide by 4.5
  // to land just past 1 for the largest events; clamp to keep the colour
  // valid.
  const t = Math.min(1, Math.log10(cases + 1) / 4.5);
  // Three-stop gradient through HSL-friendly RGB:
  //   t=0.0 → #facc15 (yellow-300)
  //   t=0.5 → #ef4444 (red-500)
  //   t=1.0 → #7f1d1d (red-900)
  const stops: Array<[number, [number, number, number]]> = [
    [0.0, [250, 204, 21]],
    [0.5, [239, 68, 68]],
    [1.0, [127, 29, 29]],
  ];
  const [r, g, b] = interpolateRgb(stops, t);
  // Opacity ramps with intensity so faint events don't drown the basemap.
  const alpha = 0.28 + 0.40 * t;
  return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${alpha.toFixed(3)})`;
}

function interpolateRgb(
  stops: Array<[number, [number, number, number]]>,
  t: number,
): [number, number, number] {
  for (let i = 0; i < stops.length - 1; i++) {
    const [t0, c0] = stops[i];
    const [t1, c1] = stops[i + 1];
    if (t >= t0 && t <= t1) {
      const u = (t - t0) / (t1 - t0);
      return [c0[0] + (c1[0] - c0[0]) * u, c0[1] + (c1[1] - c0[1]) * u, c0[2] + (c1[2] - c0[2]) * u];
    }
  }
  return stops[stops.length - 1][1];
}

function toFeatureCollection(rows: EventRecord[]): GeoJSON.FeatureCollection<GeoJSON.Point> {
  return {
    type: 'FeatureCollection',
    features: rows.map(o => {
      const total = totalCases(o);
      return {
        type: 'Feature',
        id: o.id,
        properties: {
          id: o.id,
          category: o.category,
          status: o.status,
          confidence: o.confidence,
          radius: markerRadius(total),
          // log scale for heatmap weight so a single 3000-case epidemic
          // doesn't drown out 30 small events
          weight: Math.log10(Math.max(total, 1) + 1),
          color: CATEGORY_COLOR[o.category as Category] ?? '#a8a29e',
          active: o.status === 'active' ? 1 : 0,
        },
        geometry: { type: 'Point', coordinates: [o.location.lng, o.location.lat] },
      };
    }),
  };
}

export function EventMap({
  events, selectedId, onSelect, viewMode = 'markers', initialCamera,
  statusDim = 'burden', countryStatus = {},
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const styleLoadedRef = useRef(false);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const viewModeRef = useRef(viewMode);
  viewModeRef.current = viewMode;
  // Mirror events into a ref so the (mount-time) style.load handler
  // can re-push the *current* dataset after setStyle wipes the layers,
  // not the empty array that was in scope when the map mounted.
  const eventsRef = useRef(events);
  eventsRef.current = events;
  // Mirror statusDim + countryStatus into refs so the style.load closure
  // (and the dimension-change effect below) always see the current values.
  const statusDimRef = useRef(statusDim);
  statusDimRef.current = statusDim;
  const countryStatusRef = useRef(countryStatus);
  countryStatusRef.current = countryStatus;
  // Track the style URL we last asked for so we don't redundantly call
  // setStyle (which re-loads the entire basemap).
  const currentStyleRef = useRef<string>(STYLE_SATELLITE);
  const pulseTRef = useRef(0);

  useEffect(() => {
    const token = import.meta.env.VITE_MAPBOX_TOKEN;
    if (!token || !containerRef.current) return;
    mapboxgl.accessToken = token;

    // Initial style picks up the URL-driven viewMode so deep-linking with
    // `?v=choropleth` lands users on the light basemap immediately,
    // skipping the satellite-then-flash transition.
    const initialStyle = styleForMode(viewMode);
    currentStyleRef.current = initialStyle;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: initialStyle,
      center: initialCamera ? [initialCamera.lng, initialCamera.lat] : [-15, 25],
      zoom: initialCamera ? initialCamera.zoom : 1.7,
      minZoom: 1.2,
      maxZoom: 19,
      attributionControl: false,
      logoPosition: 'bottom-left',
      projection: { name: 'globe' },
      pitchWithRotate: false,
      dragRotate: false,
    });
    map.scrollZoom.setWheelZoomRate(1 / 220);
    map.touchZoomRotate.disableRotation();
    // Zoom controls move to bottom-left (next to Mapbox logo) to free the
    // bottom-right corner for the "Maps by Micky" credit + attribution.
    map.addControl(
      new mapboxgl.NavigationControl({ showCompass: false, showZoom: true }),
      'bottom-left',
    );
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');
    mapRef.current = map;

    // style.load fires on first load AND every subsequent setStyle. Each
    // time, every source/layer the previous style had is gone — so we
    // re-install everything from scratch and push the CURRENT dataset
    // (via eventsRef, not the closure from mount time).
    map.on('style.load', () => {
      styleLoadedRef.current = true;
      restyleBaseMap(map);
      installLayers(map);
      pushData(map, eventsRef.current, statusDimRef.current, countryStatusRef.current);
      wireInteractions(map, onSelectRef);
      // Re-apply the selected event's filter on the selected-ring
      // layer so a setStyle while a card is open keeps the highlight.
      const sel = (mapRef.current as MapboxMap | null)?.getStyle();
      if (sel) {
        try {
          map.setFilter(LAYER_SELECTED, ['==', ['get', 'id'], '__none__']);
        } catch { /* layer not present yet */ }
      }
      applyViewMode(map, viewModeRef.current);
    });

    // Active-event pulse — modulates the radius/opacity in place.
    const pulseHandle = setInterval(() => {
      if (!mapRef.current || !styleLoadedRef.current) return;
      pulseTRef.current = (pulseTRef.current + 1) % 70;
      const phase = pulseTRef.current / 70;
      const k = 1.4 + phase * 2.4;
      const op = 0.42 * (1 - phase);
      try {
        mapRef.current.setPaintProperty(LAYER_PULSE, 'circle-radius', ['*', ['get', 'radius'], k]);
        mapRef.current.setPaintProperty(LAYER_PULSE, 'circle-opacity', op);
      } catch { /* style not yet loaded */ }
    }, 50);

    return () => {
      clearInterval(pulseHandle);
      map.remove();
      mapRef.current = null;
      styleLoadedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleLoadedRef.current) return;
    pushData(map, events, statusDim, countryStatus);
  }, [events, statusDim, countryStatus]);

  // ----- view-mode toggle: markers vs heatmap vs choropleth ------------
  // Choropleth swaps to a flat light basemap so the country fills read
  // clearly; the other two stay on the satellite globe. setStyle wipes
  // every source/layer Mapbox knows about, so we let the existing
  // `style.load` handler re-install + re-push data + re-apply the view
  // mode. For same-style transitions we just toggle layer visibility.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const desiredStyle = styleForMode(viewMode);
    if (desiredStyle !== currentStyleRef.current) {
      currentStyleRef.current = desiredStyle;
      styleLoadedRef.current = false;
      map.setStyle(desiredStyle);
      return;
    }
    if (!styleLoadedRef.current) return;
    applyViewMode(map, viewMode);
  }, [viewMode]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleLoadedRef.current) return;
    map.setFilter(LAYER_SELECTED, ['==', ['get', 'id'], selectedId ?? '__none__']);

    if (selectedId) {
      const o = events.find(r => r.id === selectedId);
      if (o) {
        map.flyTo({
          center: [o.location.lng, o.location.lat],
          zoom: Math.max(map.getZoom(), 3.6),
          speed: 0.9,
          curve: 1.4,
          essential: true,
        });
      }
    }
  }, [selectedId, events]);

  return (
    <div ref={containerRef} className="absolute inset-0 h-full w-full" aria-label="map of events" />
  );
}

// =============================================================================
// helpers
// =============================================================================

/** Tune the satellite-streets style: subdue road/admin overlays, lift labels
 *  for legibility against earth imagery, harmonize with cosmic chrome. */
function restyleBaseMap(map: MapboxMap) {
  const tweaks: Array<[string, string, unknown]> = [
    // softening the dense overlays — keep major arterials, hide minor roads
    ['road-minor', 'line-opacity', 0],
    ['road-street', 'line-opacity', 0],
    ['road-secondary-tertiary', 'line-opacity', 0.25],
    ['road-primary', 'line-opacity', 0.4],
    ['road-motorway-trunk', 'line-opacity', 0.55],
    ['road-rail', 'line-opacity', 0],
    ['tunnel-minor', 'line-opacity', 0],
    ['tunnel-street', 'line-opacity', 0],
    // borders: keep country lines visible, drop subdivisions
    ['admin-1-boundary', 'line-opacity', 0.18],
    ['admin-1-boundary-bg', 'line-opacity', 0],
    ['admin-0-boundary', 'line-color', '#ffffff'],
    ['admin-0-boundary', 'line-opacity', 0.35],
    ['admin-0-boundary-bg', 'line-opacity', 0.0],
    // labels: country names crisp white with strong dark halo
    ['country-label', 'text-color', '#ffffff'],
    ['country-label', 'text-halo-color', 'rgba(0,0,0,0.7)'],
    ['country-label', 'text-halo-width', 1.6],
    ['country-label', 'text-halo-blur', 0.2],
    ['settlement-major-label', 'text-color', '#f5f5f4'],
    ['settlement-major-label', 'text-halo-color', 'rgba(0,0,0,0.6)'],
    ['settlement-major-label', 'text-halo-width', 1.2],
    ['settlement-minor-label', 'text-color', '#d6d3d1'],
    ['settlement-minor-label', 'text-halo-color', 'rgba(0,0,0,0.5)'],
    ['settlement-minor-label', 'text-halo-width', 1],
    ['settlement-subdivision-label', 'text-opacity', 0.4],
    // water labels: low key
    ['waterway-label', 'text-opacity', 0.5],
    ['water-point-label', 'text-opacity', 0.5],
  ];
  for (const [layer, prop, val] of tweaks) {
    // Guard with getLayer so absent layers don't trigger Mapbox's internal
    // error log (the try/catch swallows the throw but Mapbox still console.errors).
    if (!map.getLayer(layer)) continue;
    try { (map.setPaintProperty as (l: string, p: string, v: unknown) => void)(layer, prop, val); }
    catch { /* layer absent or property invalid */ }
  }
  // Atmosphere — Earth-from-orbit. Three-tone gradient with a warm-sunlit
  // horizon ring, soft Earth-blue upper atmosphere, deep cosmic space, and a
  // generous starfield. The light-theme cartography sits inside this dome,
  // glass chrome floats above it.
  try {
    map.setFog({
      // distance fall-off — fog is thin near camera, denser at horizon
      range: [0.5, 10],
      // horizon glow ring (warm, sunlit-edge)
      color: '#ffe7b5',
      // upper atmosphere visible just above the limb (Earth-blue)
      'high-color': '#5b8fc7',
      // deep space — slight blue-violet tint, not pure black
      'space-color': '#070612',
      'horizon-blend': 0.07,
      'star-intensity': 0.85,
    });
  } catch { /* projection may not support fog */ }
}

/**
 * Returns the id of the first symbol layer in the current Mapbox style
 * (i.e. the first label layer). Used as a `beforeId` so any custom fill /
 * line / circle layer we add stays underneath country, place, and road
 * labels rather than covering them.
 */
function firstLabelLayerId(map: MapboxMap): string | undefined {
  const style = map.getStyle();
  if (!style?.layers) return undefined;
  for (const layer of style.layers) {
    if (layer.type === 'symbol') return layer.id;
  }
  return undefined;
}

function installLayers(map: MapboxMap) {
  if (map.getSource(SOURCE)) return;
  map.addSource(SOURCE, {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
    cluster: true,
    clusterMaxZoom: 4,
    clusterRadius: 32,
  });
  map.addSource(SOURCE_FLAT, {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
    cluster: false,
  });
  // Mapbox-hosted country polygons keyed by iso_3166_1 (ISO-2). Free tier;
  // every Mapbox style ships access to it. Used by the choropleth view.
  if (!map.getSource(SOURCE_COUNTRIES)) {
    map.addSource(SOURCE_COUNTRIES, {
      type: 'vector',
      url: 'mapbox://mapbox.country-boundaries-v1',
    });
  }

  // ----- country choropleth (rendered ABOVE the basemap but BELOW labels
  //       so country/place labels stay readable over the shaded fill) -----
  //
  // We insert the layer immediately before the first symbol layer (= labels)
  // in the current style. Mapbox stacks layers bottom-to-top, so passing the
  // label layer as beforeId makes our fill render under it — i.e. labels
  // sit ON TOP of the choropleth.
  const labelBeforeId = firstLabelLayerId(map);
  map.addLayer({
    id: LAYER_CHOROPLETH,
    type: 'fill',
    source: SOURCE_COUNTRIES,
    'source-layer': 'country_boundaries',
    layout: { visibility: 'none' },
    paint: {
      // The `match` expression starts off as a transparent default; it gets
      // populated the moment event data is pushed via setChoropleth().
      'fill-color': 'rgba(156, 163, 175, 0.0)',
      'fill-antialias': true,
    },
  }, labelBeforeId);
  map.addLayer({
    id: LAYER_CHOROPLETH_OUTLINE,
    type: 'line',
    source: SOURCE_COUNTRIES,
    'source-layer': 'country_boundaries',
    layout: { visibility: 'none' },
    paint: {
      // Faint outline for countries that have any case count. Set via
      // setChoropleth() so it stays in sync with the fill expression.
      'line-color': 'rgba(82, 71, 229, 0.0)',
      'line-width': 0.8,
    },
  }, labelBeforeId);

  // ----- heatmap (point-density blur, rendered below labels) -----
  //
  // Smoother than the previous build: alpha ramps up gradually from 0 so
  // there's no hard "first stop" edge, the colour ramp uses cubic-bezier
  // interpolation, the radius is larger so blooms blend across the map.
  // Inserted before the first label layer so country/place labels stay
  // readable over the heat blur.
  map.addLayer({
    id: LAYER_HEATMAP,
    type: 'heatmap',
    source: SOURCE_FLAT,
    layout: { visibility: 'none' },
    paint: {
      'heatmap-weight': [
        'interpolate', ['linear'], ['get', 'weight'],
        0, 0.5,
        4, 1,
      ],
      // Gentler intensity ramp — was 3 → 1.8, now 1.8 → 1.2 — keeps blooms
      // soft instead of saturating to a hard red core at low zoom.
      'heatmap-intensity': [
        'interpolate', ['linear'], ['zoom'],
        0, 1.8,
        4, 1.6,
        8, 1.4,
        12, 1.25,
        16, 1.15,
      ],
      // Cubic-bezier interpolation between density stops gives noticeably
      // smoother transitions than 'linear' — eight stops on a warm thermal
      // ramp (yellow → orange → red → deep red) so the first stop fades in
      // gradually from transparent instead of jumping straight to indigo.
      'heatmap-color': [
        'interpolate', ['cubic-bezier', 0.4, 0, 0.6, 1], ['heatmap-density'],
        0,    'rgba(0,0,0,0)',
        0.10, 'rgba(254, 240, 138, 0.25)',
        0.25, 'rgba(252, 211, 77, 0.55)',
        0.40, 'rgba(251, 146, 60, 0.7)',
        0.55, 'rgba(239, 68, 68, 0.82)',
        0.70, 'rgba(220, 38, 38, 0.9)',
        0.85, 'rgba(185, 28, 28, 0.95)',
        1,    'rgba(127, 29, 29, 1)',
      ],
      // Larger radius across the board → softer, broader blooms that blend
      // into neighbouring events instead of stacking as sharp discs.
      'heatmap-radius': [
        'interpolate', ['linear'], ['zoom'],
        0, 70,
        4, 70,
        8, 75,
        12, 85,
        16, 100,
      ],
      // Mostly-opaque across zooms; gentle taper at extreme zoom so the
      // satellite imagery underneath stays readable
      'heatmap-opacity': [
        'interpolate', ['linear'], ['zoom'],
        0, 0.92,
        12, 0.82,
        16, 0.7,
      ],
    },
  }, labelBeforeId);

  // ----- clusters: glow + glass disc + indigo ring + count -----
  map.addLayer({
    id: LAYER_CLUSTER_GLOW,
    type: 'circle',
    source: SOURCE,
    filter: ['has', 'point_count'],
    paint: {
      // soft outer glow — picks up the indigo accent
      'circle-color': '#5247e5',
      'circle-radius': ['step', ['get', 'point_count'], 22, 5, 26, 12, 32],
      'circle-opacity': 0.15,
      'circle-blur': 0.9,
    },
  });
  map.addLayer({
    id: LAYER_CLUSTER_BG,
    type: 'circle',
    source: SOURCE,
    filter: ['has', 'point_count'],
    paint: {
      // glass disc — warm cream, very high opacity for legibility on dark space
      'circle-color': '#ffffff',
      'circle-radius': ['step', ['get', 'point_count'], 16, 5, 20, 12, 24],
      'circle-opacity': 0.97,
      'circle-stroke-color': 'rgba(82, 71, 229, 0.55)',
      'circle-stroke-width': 1.2,
    },
  });
  map.addLayer({
    id: LAYER_CLUSTER_RING,
    type: 'circle',
    source: SOURCE,
    filter: ['has', 'point_count'],
    paint: {
      // inner highlight ring — a hairline of accent inside the disc edge
      'circle-color': 'rgba(0,0,0,0)',
      'circle-radius': ['step', ['get', 'point_count'], 13, 5, 17, 12, 21],
      'circle-stroke-color': '#5247e5',
      'circle-stroke-width': 0.75,
      'circle-stroke-opacity': 0.18,
    },
  });
  map.addLayer({
    id: LAYER_CLUSTER_COUNT,
    type: 'symbol',
    source: SOURCE,
    filter: ['has', 'point_count'],
    layout: {
      'text-field': ['get', 'point_count_abbreviated'],
      'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
      'text-size': 13,
      'text-allow-overlap': true,
      'text-offset': [0, 0.05],
    },
    paint: {
      'text-color': '#0c0a09',
    },
  });

  // ----- soft drop shadow under every point (cheap; visible on light theme) -----
  map.addLayer({
    id: LAYER_SHADOW,
    type: 'circle',
    source: SOURCE,
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': '#0c0a09',
      'circle-radius': ['+', ['get', 'radius'], 1],
      'circle-opacity': 0.10,
      'circle-blur': 1.2,
      'circle-translate': [0, 1],
    },
  });

  // ----- pulse: active events only — animated by component setInterval -----
  map.addLayer({
    id: LAYER_PULSE,
    type: 'circle',
    source: SOURCE,
    filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'active'], 1]],
    paint: {
      'circle-color': '#dc2626',
      'circle-radius': ['*', ['get', 'radius'], 1.4],
      'circle-opacity': 0.4,
      'circle-blur': 0.5,
    },
  });

  // ----- selected: indigo ring around the selected feature -----
  map.addLayer({
    id: LAYER_SELECTED,
    type: 'circle',
    source: SOURCE,
    filter: ['==', ['get', 'id'], '__none__'],
    paint: {
      'circle-color': 'rgba(0,0,0,0)',
      'circle-radius': ['+', ['get', 'radius'], 8],
      'circle-stroke-color': '#5247e5',
      'circle-stroke-width': 2,
      'circle-stroke-opacity': 0.9,
    },
  });

  // ----- base point: white-ringed colored dot -----
  map.addLayer({
    id: LAYER_BASE,
    type: 'circle',
    source: SOURCE,
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': ['get', 'color'],
      'circle-radius': ['get', 'radius'],
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 1.75,
      'circle-opacity': 1,
      'circle-radius-transition': { duration: 200 },
    },
  });
}

function applyViewMode(map: MapboxMap, mode: 'markers' | 'heatmap' | 'choropleth') {
  const heatmapVis = mode === 'heatmap' ? 'visible' : 'none';
  const choroplethVis = mode === 'choropleth' ? 'visible' : 'none';
  // Markers always show in marker mode; in choropleth mode we keep them
  // visible so users can still click into individual events on top of the
  // country fill. Heatmap mode hides markers (they'd clash with the
  // bloom).
  const markersVis = mode === 'heatmap' ? 'none' : 'visible';
  try { map.setLayoutProperty(LAYER_HEATMAP, 'visibility', heatmapVis); } catch { /* */ }
  for (const id of CHOROPLETH_LAYERS) {
    try { map.setLayoutProperty(id, 'visibility', choroplethVis); } catch { /* */ }
  }
  for (const id of MARKER_LAYERS) {
    try { map.setLayoutProperty(id, 'visibility', markersVis); } catch { /* */ }
  }
}

function pushData(
  map: MapboxMap,
  rows: EventRecord[],
  statusDim: StatusDimension = 'burden',
  countryStatus: Record<string, CountryStatusRecord> = {},
) {
  const fc = toFeatureCollection(rows);
  const clustered = map.getSource(SOURCE) as GeoJSONSource | undefined;
  if (clustered) clustered.setData(fc);
  const flat = map.getSource(SOURCE_FLAT) as GeoJSONSource | undefined;
  if (flat) flat.setData(fc);
  setChoropleth(map, rows, statusDim, countryStatus);
}

function setChoropleth(
  map: MapboxMap,
  rows: EventRecord[],
  statusDim: StatusDimension = 'burden',
  countryStatus: Record<string, CountryStatusRecord> = {},
) {
  // TODO(cage-bend): Multi-dimensional choropleth fill.
  // When countryStatus has records, use the selected dimension to colour
  // countries. Currently falls back to the events-based case-count fill
  // for all dimensions except 'burden'. A future pass should implement
  // full fill expressions for 'recency', 'vaccine_readiness', and
  // 'preparedness' once the data has enough coverage to be meaningful.
  //
  // 'burden' uses the countryStatus.burden.outbreak_count if available,
  // otherwise falls back to the events-derived case count (the original
  // choropleth behaviour — safe for v1 with a single CD record).

  let fillExpr: mapboxgl.ExpressionSpecification | string;
  let outlineExpr: mapboxgl.ExpressionSpecification | string;

  if (statusDim !== 'burden' && Object.keys(countryStatus).length > 0) {
    // TODO(cage-bend): implement fill expressions for recency, vaccine_readiness,
    // preparedness when countryStatus data reaches multi-country coverage.
    fillExpr = 'rgba(156, 163, 175, 0.12)';
    outlineExpr = 'rgba(82, 71, 229, 0.0)';
  } else {
    // Default: events-derived case-count choropleth (original behaviour).
    const byCountry = casesByCountry(rows);
    fillExpr = buildChoroplethColorExpression(byCountry);
    if (byCountry.size === 0) {
      outlineExpr = 'rgba(82, 71, 229, 0.0)';
    } else {
      const matchExpr: unknown[] = ['match', ['get', 'iso_3166_1']];
      for (const code of byCountry.keys()) matchExpr.push(code, 'rgba(82, 71, 229, 0.55)');
      matchExpr.push('rgba(82, 71, 229, 0.0)');
      outlineExpr = matchExpr as unknown as mapboxgl.ExpressionSpecification;
    }
  }

  try {
    map.setPaintProperty(LAYER_CHOROPLETH, 'fill-color', fillExpr as mapboxgl.ExpressionSpecification);
    map.setPaintProperty(LAYER_CHOROPLETH_OUTLINE, 'line-color', outlineExpr as mapboxgl.ExpressionSpecification);
  } catch { /* layers not installed yet — pushData will retry on next render */ }
}

function wireInteractions(map: MapboxMap, onSelectRef: React.MutableRefObject<(id: string | null) => void>) {
  for (const layer of [LAYER_BASE, LAYER_CLUSTER_BG]) {
    map.on('mouseenter', layer, () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', layer, () => { map.getCanvas().style.cursor = ''; });
  }

  map.on('click', LAYER_BASE, (e) => {
    const f = e.features?.[0];
    if (!f) return;
    const id = (f.properties as { id?: string } | null)?.id;
    if (id) onSelectRef.current(id);
  });

  map.on('click', LAYER_CLUSTER_BG, (e) => {
    const f = e.features?.[0];
    if (!f) return;
    const clusterId = (f.properties as { cluster_id?: number } | null)?.cluster_id;
    const src = map.getSource(SOURCE) as GeoJSONSource;
    if (clusterId === undefined) return;
    src.getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err || zoom == null) return;
      map.easeTo({
        center: (f.geometry as GeoJSON.Point).coordinates as [number, number],
        zoom: zoom + 0.4,
        duration: 600,
      });
    });
  });

  map.on('click', (e) => {
    const features = map.queryRenderedFeatures(e.point, {
      layers: [LAYER_BASE, LAYER_CLUSTER_BG],
    });
    if (!features.length) onSelectRef.current(null);
  });
}
